import React from "react";
import { Authenticated, Refine } from "@refinedev/core";
import { KBarProvider } from "@refinedev/kbar";
import * as Sentry from "@sentry/react";
import {
  ErrorComponent,
  useNotificationProvider,
  ThemedLayout,
  RefineSnackbarProvider,
} from "@refinedev/mui";
import GlobalStyles from "@mui/material/GlobalStyles";
import CssBaseline from "@mui/material/CssBaseline";
import CircularProgress from "@mui/material/CircularProgress";
import dataProvider from "@refinedev/simple-rest";
import routerProvider from "@refinedev/react-router";
import {
  CatchAllNavigate,
  NavigateToResource,
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from "@refinedev/react-router";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import Box from "@mui/material/Box";
import { AuthPage } from "./pages/auth";
import { OnboardingPage } from "./pages/onboarding";
import { UserProfile } from "./pages/profile";
import { PlacesList } from "./pages/places";
import { PlaceCreate } from "./pages/places/create";
import { PropertiesList } from "./pages/properties";
import { PropertyDetails } from "./pages/property-details/details";
import { BillingTransactionsPage } from "./pages/billing/transactions";
import { BillingInvoicesPage } from "./pages/billing/invoices";
import { DevSettingsPage } from "./pages/dev-settings";
import SentryDebugPage from "./pages/dev-settings/sentry-debug";
import { ProfilesList } from "./pages/profiles";
import { ColorModeContextProvider } from "./contexts";
import { Header, Title, CustomSider } from "./components";
import { SentryErrorFallback } from "./components/error-boundary/SentryErrorFallback";
import { ProtectedRoute } from "./components/ProtectedRoute";

import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";
import { getlostFirebaseAuth } from "./utils/auth/firebaseConfig";
import { trackNavigation } from "./utils/sentry-breadcrumbs";
import { trackPageView } from "./utils/analytics-events";

const API_URL = "https://api.finefoods.refine.dev";

// Custom loading component that preserves current location
const AuthLoadingComponent: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  
  return (
    <ThemedLayout 
      Header={Header} 
      Title={Title}
      Sider={CustomSider}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '70vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress />
        <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          {t('auth.states.authenticating')}
        </Box>
      </Box>
    </ThemedLayout>
  );
};

// Component to track route changes for Sentry breadcrumbs and Google Analytics page views
const RouteTracker: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  
  React.useEffect(() => {
    // Track for Sentry breadcrumbs
    trackNavigation(location.pathname, { search: location.search, hash: location.hash });
    
    // Track page views to Google Analytics
    const getPageTitle = (): string => {
      const pathname = location.pathname;
      if (pathname === '/properties') return t('properties.resourceLabel');
      if (pathname.startsWith('/properties/')) return t('common.details');
      if (pathname === '/places') return t('places.resourceLabel');
      if (pathname === '/places/new') return 'Create Place';
      if (pathname === '/billing/transactions') return t('billing.transactions.resourceLabel');
      if (pathname === '/billing/invoices') return t('billing.invoices.resourceLabel');
      if (pathname === '/profile') return 'User Profile';
      if (pathname === '/onboarding') return 'Onboarding';
      if (pathname === '/dev-settings') return 'Dev Settings';
      if (pathname === '/login') return 'Login';
      return 'Page';
    };
    
    trackPageView(getPageTitle(), location.pathname);
  }, [location.pathname, t]);
  
  return null;
};

const App: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isDevEnv = import.meta.env.VITE_ENV === 'dev';
  
  const i18nProvider = {
    translate: (key: string, params: object) => t(key, params),
    changeLocale: (lang: string) => i18n.changeLanguage((lang || 'en').split('-')[0]),
    getLocale: () => (i18n.language || 'en').split('-')[0],
  };

  // Define all resources
  const allResources = [
    // Profile is rendered as custom menu item in CustomSider
    {
      name: "properties",
      list: "/properties",
      meta: {
        label: t('properties.resourceLabel'),
        icon: <HomeIcon />,
      },
    },
    {
      name: "places",
      list: "/places",
      meta: {
        label: t('places.resourceLabel'),
        icon: <LocationOnIcon />,
      },
    },
    {
      name: "billing-transactions",
      list: "/billing/transactions",
      meta: {
        label: t('billing.transactions.resourceLabel'),
        icon: <AttachMoneyIcon />,
      },
    },
    {
      name: "billing-invoices",
      list: "/billing/invoices",
      meta: {
        label: t('billing.invoices.resourceLabel'),
        icon: <ReceiptLongIcon />,
      },
    },
    // Dev Settings - only visible in development mode
    ...(import.meta.env.DEV
      ? [{
          name: "dev-settings",
          list: "/dev-settings",
          meta: {
            label: "Dev Settings",
            icon: <SettingsIcon />,
          },
        }]
      : []),
    // Profiles menu - only visible for admin users
    {
      name: "profiles",
      list: "/profiles",
      meta: {
        label: t('profiles.resourceLabel'),
        icon: <AccountCircleOutlinedIcon />,
      },
    },
    // Dev Settings removed
  ];

  return (
    <BrowserRouter>
      <KBarProvider>
        <ColorModeContextProvider>
          <CssBaseline />
          <GlobalStyles
            styles={(theme) => ({
              html: { WebkitFontSmoothing: "auto" },
              "html, body, #root": {
                backgroundColor: theme.palette.background?.default ?? "#121212",
                minHeight: "100%",
              },
            })}
          />
          <RefineSnackbarProvider>
            <Sentry.ErrorBoundary
              fallback={(errorData) => (
                <SentryErrorFallback
                  error={errorData.error as Error}
                  resetError={errorData.resetError}
                  componentStack={errorData.componentStack}
                />
              )}
              beforeCapture={(scope) => {
                scope.setTag("priority", "critical");
              }}
            >
              <Refine
                routerProvider={routerProvider}
                dataProvider={dataProvider(API_URL)}
                authProvider={getlostFirebaseAuth.getAuthProvider()}
                i18nProvider={i18nProvider}
                options={{
                  syncWithLocation: true,
                  warnWhenUnsavedChanges: true,
                  breadcrumb: false,
                  disableTelemetry: true,
                }}
                notificationProvider={useNotificationProvider}
                resources={allResources}
              >
                <RouteTracker />
                <Routes>
                <Route
                  element={
                    <Authenticated
                      key="authenticated-routes"
                      fallback={<CatchAllNavigate to="/login" />}
                      loading={<AuthLoadingComponent />}
                    >
                      <ThemedLayout 
                        Header={Header} 
                        Title={Title}
                        Sider={CustomSider}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            minHeight: "100vh",
                            bgcolor: "background.default",
                          }}
                        >
                          <Outlet />
                        </Box>
                      </ThemedLayout>
                    </Authenticated>
                  }
                >
                  {/* Default route uses NavigateToResource (no page replacement) */}
                  <Route index element={<NavigateToResource resource="properties" />} />

                  <Route path="/properties/:id" element={<PropertyDetails />} />
                  <Route path="/properties/new" element={<PropertyDetails />} />
                  <Route path="/properties" element={<PropertiesList />} />

                  <Route path="/places/new" element={<PlaceCreate />} />
                  <Route path="/places" element={<PlacesList />} />

                  <Route path="/billing/transactions" element={<BillingTransactionsPage />} />
                  <Route path="/billing/invoices" element={<BillingInvoicesPage />} />

                  {/* Profiles - hidden for all users */}
                  <Route path="/profiles" element={
                    <ProtectedRoute requireFullAccess={true}>
                      <ProfilesList />
                    </ProtectedRoute>
                  } />

                  {/* Dev Settings - only visible in development mode */}
                  {import.meta.env.DEV && (
                    <>
                      <Route path="/dev-settings" element={<DevSettingsPage />} />
                      <Route path="/dev-settings/sentry-debug" element={<SentryDebugPage />} />
                    </>
                  )}

                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute requireFullAccess={true}>
                        <UserProfile />
                      </ProtectedRoute>
                    }
                  />

                  {/* Dev Settings route removed */}

                  {/* Catch-all inside authenticated layout to avoid white background */}
                  <Route path="*" element={<ErrorComponent />} />
                </Route>

                <Route
                  element={
                    <Authenticated key="auth-pages" fallback={<Outlet />}>
                      <NavigateToResource resource="properties" />
                    </Authenticated>
                  }
                >
                  <Route
                    path="/login"
                    element={
                      <AuthPage
                        type="login"
                        hideForm={true}
                        providers={[
                          {
                            name: "google",
                            label: t("auth.providers.google"),
                            icon: (
                              <GoogleIcon
                                style={{
                                  fontSize: 24,
                                }}
                              />
                            ),
                          },
                          {
                            name: "apple",
                            label: t("auth.providers.apple"),
                            icon: (
                              <AppleIcon
                                style={{
                                  fontSize: 24,
                                }}
                              />
                            ),
                          }
                        ]}
                      />
                    }
                  />
                  {/* Onboarding route - unauthenticated users can access after Firebase auth */}
                  <Route path="/onboarding" element={<OnboardingPage />} />
                </Route>

                {/* Onboarding route - accessible even when authenticated */}
                <Route
                  path="/onboarding"
                  element={<OnboardingPage />}
                />

                <Route
                  element={
                    <Authenticated key="catch-all">
                      <ThemedLayout Header={Header} Title={Title} Sider={CustomSider}>
                        <Outlet />
                      </ThemedLayout>
                    </Authenticated>
                  }
                >
                  <Route path="*" element={<ErrorComponent />} />
                </Route>
              </Routes>
              <UnsavedChangesNotifier />
              <DocumentTitleHandler handler={({ pathname }) => {
                const defaultTitle = "GetLost - Guidebooks";
                if (!pathname) return defaultTitle;
                if (pathname === "/") return defaultTitle;
                if (pathname === "/login") return "Login | GetLost";
                if (pathname === "/onboarding") return "Onboarding | GetLost";
                if (pathname.startsWith("/profile")) return "Profile | GetLost";
                if (pathname.includes("/properties")) return "Properties | GetLost";
                if (pathname.includes("/places")) return "Places | GetLost";
                if (pathname.includes("/dev-settings")) return "Dev Settings | GetLost";
                return defaultTitle;
              }} />
            </Refine>
            </Sentry.ErrorBoundary>
          </RefineSnackbarProvider>
        </ColorModeContextProvider>
      </KBarProvider>
    </BrowserRouter>
  );
};

export default App;
